import { Injectable, inject } from "@angular/core";
import { HttpRequestService } from "../../../../../shared/services/http-request/http-request.service";
import { API } from "../../../../../shared/constants/api-url";
import { BehaviorSubject, Observable, tap } from "rxjs";
import { IProvider } from "../../contract/contracts/models/contracts.model";
import { Store } from "@ngxs/store";
import { PatchProviderList } from "../../../../../shared/store/provider/provider.actions";
import { DataGridFilterCondition } from "../../../../../shared/constants/datagrid-filter-condition.enum";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { TranslationService } from "@waseel-asp/tpa-waseel-lib-staging";
import { BANK_ACCOUNT_REGEX, EMAIL_REGEX, IBAN_REGEX, WEBSITE_REGEX, ZIPCODE_REGEX } from "../../../../../shared/constants/system-constants.model";
import { providerConfig } from "../models/provider.model";
import { HelperService } from "../../../../../shared/services/common/helper.service";
import { GenderType, GenderTypeValue } from "../../../../../shared/constants/gender-enum";

@Injectable({
  providedIn: 'root'
})
export class ProviderService {
  private store = inject(Store);
  private httpRequestService = inject(HttpRequestService);
  private translation = inject(TranslationService);
  private helperService = inject(HelperService);

  private providerListSubject = new BehaviorSubject<IProvider[]>([]);
  providerList$ = this.providerListSubject.asObservable();

  getProviderListByIds(ids: string[]): Observable<unknown> {
    const payload = {
      columns: [
        {
          name: "providerId",
          searchable: true,
          orderable: true,
          filterable: true,
          filterSearch: {
            value: ids?.length === 1 ? ids[0] : ids?.join(','),
            condition: DataGridFilterCondition.IsEqualTo
          },
          excelColumnName: "providerId"
        }
      ],
      defaultSortingColumn: "providerId",
      pageNumber: 0,
      pageSize: 0,
      sortBy: "providerId",
      sortOrder: "asc",
      generalSearch: "",
      isSearchByCriteria: false
    };
    return this.httpRequestService.post(API.PROVIDER.PROVIDER + 'list', payload);
  }

  getcolumnsKeys(): string[] {
    return [
      'nameEnglish',
      'nameArabic'
    ];
  }

  setStoreProviders(ids: string[]) {
    if (!ids || ids.length === 0) {
      this.updateStore(null);
      return;
    }
    this.getProviderListByIds(ids).pipe(
      tap((providers: IProvider[]) => {
        this.updateStore(providers);
      })
    ).subscribe();
  }

  clearStore() {
    this.updateStore(null);
  }

  getProviderFormGroup() {
    return new FormGroup({
      nameArabic: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
      nameEnglish: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
      providerTypeId: new FormControl(null),
      crNo: new FormControl(null, [Validators.maxLength(50)]),
      mohLicenseNumber: new FormControl(null, [Validators.maxLength(50)]),
      crexpiryDate: new FormControl(null),
      vatRegistrationNumber: new FormControl(null, [Validators.maxLength(20)]),
      cchiId: new FormControl(null, [Validators.maxLength(50)]),
      nphiesId: new FormControl(null, [Validators.maxLength(30)]),
      providerURL: new FormControl(null, [Validators.maxLength(50), this.helperService.customPatternValidation(WEBSITE_REGEX, this.translation.instant('COMMON.IS_NOT_VALID', { value: this.translation.instant(providerConfig.providerURL) }))]),
      cchiexpiryDate: new FormControl(null),
      contact: new FormGroup({
        contactId: new FormControl(null),
        mobileNumber: new FormControl('', [Validators.required]),
        telephone: new FormControl(''),
        emergencyNumber: new FormControl(''),
        contactNumber: new FormControl(''),
        email: new FormControl(null, [Validators.required, Validators.maxLength(40), this.helperService.customPatternValidation(EMAIL_REGEX, this.translation.instant('COMMON.EMAIL_VALIDATION', { value: this.translation.instant(providerConfig.email) }))]),
        website: new FormControl('', [Validators.maxLength(50), this.helperService.customPatternValidation(WEBSITE_REGEX, this.translation.instant('COMMON.IS_NOT_VALID', { value: this.translation.instant(providerConfig.website) }))]),
        fax: new FormControl('')
      }),
      address: new FormGroup({
        addressId: new FormControl(null),
        street: new FormControl(null, [Validators.maxLength(100)]),
        cityId: new FormControl(null, [Validators.required]),
        stateId: new FormControl(null, [Validators.required]),
        zipcode: new FormControl(null, [this.helperService.customPatternValidation(ZIPCODE_REGEX, providerConfig.zipCodeValidation)]),
        countryId: new FormControl(null, [Validators.required]),
        addressLine: new FormControl(null, [Validators.maxLength(100)]),
        latitude: new FormControl(null, [Validators.min(-90), Validators.max(90)]),
        longitude: new FormControl(null, [Validators.min(-180), Validators.max(180)])
      }),
      bankAccount: new FormGroup({
        bankAccountId: new FormControl(null),
        nameArabic: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
        nameEnglish: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
        bankAccountName: new FormControl(null, Validators.maxLength(100)),
        accountNumber: new FormControl(null, [Validators.required, this.helperService.customPatternValidation(BANK_ACCOUNT_REGEX, providerConfig.accountNumberValidation)]),
        iban: new FormControl(null, [this.helperService.customPatternValidation(IBAN_REGEX, providerConfig.ibanValidation)]),
        countryId: new FormControl(null),
        stateId: new FormControl(null),
        cityId: new FormControl(null)
      }),
      networks: new FormGroup({
        networkId: new FormControl(null)
      }),
      distanceAssessmentId: new FormControl(null, Validators.required),
      similarProviderIds: new FormControl([]),
      providerGeneralLookId: new FormControl(null, Validators.required),
      clinicIds: new FormControl([]),
      medicalDeviceIds: new FormControl([]),
      hasRadiologicalProtectionCertificate: new FormControl(null, Validators.required),
      pharmacyAvailabilityId: new FormControl(null, Validators.required),
      laboratoryAvailabilityId: new FormControl(null, Validators.required),
      sterilizationAvailabilityId: new FormControl(null, Validators.required),
      dentalLabAvailabilityId: new FormControl(null, Validators.required),
      waitingRoomFurniture: new FormArray([
        new FormGroup({
          gender: new FormControl(GenderTypeValue[GenderType.Male]),
          furnitureIds: new FormControl([])
        }),
        new FormGroup({
          gender: new FormControl(GenderTypeValue[GenderType.Female]),
          furnitureIds: new FormControl([])
        })
      ]),
      airConditioningTypeId: new FormControl(null, Validators.required),
      airConditionerConditionId: new FormControl(null, Validators.required),
      hasUseOfSehaService: new FormControl(null, Validators.required),
      sehaServicesUsed: new FormControl(null),
      hasParkingLotAvailable: new FormControl(null, Validators.required),
      numberOfParkingSpaces: new FormControl(null),
      contacts: new FormArray([], Validators.minLength(1)),
      administrationContacts: new FormArray([], Validators.minLength(1)),
      medicalContacts: new FormArray([], Validators.minLength(1))
    });
  }


  private updateStore(providers: IProvider[] | null) {
    this.providerListSubject.next(providers);
    this.store.dispatch(new PatchProviderList(providers));
  }
}